import { ButtonLink, Drawer, DRAWER_WIDTH } from '@design';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import clsx from 'clsx';
import React, { ReactNode } from 'react';

import styles from './ExpandableDrawer.module.scss';

export const EXPANDED_VALUE = '1';

export type ExpandableDrawerProps = {
  isOpen: boolean;
  handleCloseClick: () => void;
  headerButton?: ReactNode;
  subHeaderNode?: ReactNode;
  children: ReactNode;
  fullViewContent: ReactNode;
  defaultWidth?: number;
  label: ReactNode;
  className?: string;
};

export const ExpandableDrawer = ({
  className,
  isOpen,
  handleCloseClick,
  headerButton,
  subHeaderNode,
  children,
  fullViewContent,
  defaultWidth = DRAWER_WIDTH,
  label,
  ...rest
}: ExpandableDrawerProps) => {
  const { params, setQueryParams } = useQueryParams();
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  const handleExpand = () => {
    setQueryParams({ [queryKeys.drawerExpanded]: EXPANDED_VALUE });
  };

  return (
    <Drawer
      {...rest}
      className={clsx(
        styles.drawer,
        isExpanded && styles.drawerExpanded,
        className
      )}
      style={
        {
          width: isExpanded ? '90vw' : `${defaultWidth}px`,
        } as React.CSSProperties
      }
      isOpen={isOpen}
      handleCloseClick={handleCloseClick}
      headerButton={
        <div className={styles.FlexWrapper}>
          <div className={styles.headerButtons}>
            {!isExpanded && isOpen && (
              <div className={styles.floatButtonWrapper}>
                <ButtonLink
                  label={label}
                  onClick={handleExpand}
                  iconName="ChevronDoubleLeft"
                  iconSize={16}
                  iconViewBox="0 0 12 12"
                  className={styles.floatButton}
                />
              </div>
            )}
            {headerButton}
          </div>
          {isExpanded && subHeaderNode}
        </div>
      }
    >
      {isExpanded ? (
        <div className={styles.fullViewWrapper}>
          <div data-left-panel className={styles.leftPanel}>
            {fullViewContent}
          </div>
          <div data-right-panel className={styles.rightPanel}>
            {children}
          </div>
        </div>
      ) : (
        children
      )}
    </Drawer>
  );
};
